import {
  ChartDataContext,
  ChartDateRange,
  ChartLegendPosition,
  ChartTypeEnum,
} from '@finxone-platform/shared/enums';
import dayjs from 'dayjs';

export { ChartDataContext, ChartDateRange, ChartLegendPosition, ChartTypeEnum };

export enum ChartRangeEnum {
  WEEK = 'week',
  MONTH = 'month',
}

export function getMonthLabels(): string[] {
  return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
}

export function getWeekLabels(): string[] {
  return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
}

export enum ChartSegmentValueType {
  PERCENTAGE = 'percentage',
  ABSOLUTE = 'absolute',
}

export type ChartSegmentConfig = {
  inputId: string;
  color: string;
  label: string;
};

export type ChartDataMappingConfig = {
  numberOfSegments: number;
  segmentValueType: ChartSegmentValueType;
  segments: ChartSegmentConfig[];
  denominator: string;
  denominatorFillColor: string;
  denominatorLabel: string;
};

export function isCircularChartType(type: ChartTypeEnum): boolean {
  return type === ChartTypeEnum.DOUGHNUT || type === ChartTypeEnum.PIE || type === ChartTypeEnum.PROGRESS_ARC;
}

//Get css variable value
export function getCssVariableValue(cssVariable: string | undefined): string {
  if (!cssVariable) {
    return ''; //empty string
  }

  const cssVarMatch = cssVariable.match(/^var\((--[^)]+)\)$/);

  if (cssVarMatch) {
    // It's a CSS variable
    const variableName = cssVarMatch[1];
    const value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
    return value || cssVariable; // fallback if variable not found
  }

  // Already a valid color value
  return cssVariable;
}

export function getMonthDaysLabels(startDate: string, endDate: string): string[] {
  const start = dayjs(startDate, 'YYYY-MM-DD');
  const end = dayjs(endDate, 'YYYY-MM-DD');
  const totalDays = end.diff(start, 'days') + 1;

  const days: string[] = [];
  for (let i = 0; i < totalDays; i++) {
    days.push(start.clone().add(i, 'days').date().toString()); // Only the day number
  }

  return days;
}

/**
 * Generates week range labels for a given month. For example, if the month has 31 days, it will return:
 * ["1-7", "8-14", "15-21", "22-28", "29-31"]
 * If the month has 30 days, it will return:
 * ["1-7", "8-14", "15-21", "22-28", "29-30"]
 * If the month has 28 days, it will return:
 * ["1-7", "8-14", "15-21", "22-28"]
 * @param endDate - The end date of the month in 'YYYY-MM-DD' format to determine the number of days in the month.
 * @returns An array of week range labels for the month.
 */
export function getMonthWeeksLabels(endDate: string): string[] {
  const endOfRange = dayjs(endDate, 'YYYY-MM-DD');
  const lastDayOfMonth = endOfRange.date();

  const weeks: string[] = [];
  let weekStart = 1;

  while (weekStart <= lastDayOfMonth) {
    const weekEnd = Math.min(weekStart + 6, lastDayOfMonth);
    weeks.push(weekStart === weekEnd ? `${weekStart}` : `${weekStart}-${weekEnd}`);
    weekStart += 7;
  }

  return weeks;
}

export function getWeekRangeValue(weeksAgo: number): string {
  const start = dayjs().subtract(weeksAgo, 'week').day(1); // Monday
  const end = start.day(7); // Sunday
  return `${start.format('YYYY-MM-DD')},${end.format('YYYY-MM-DD')}`;
}

export function getWeekRangeLabel(weeksAgo: number): string {
  // Get the Monday of the target week (startOf('week') returns Sunday in dayjs, so add 1 day)
  const start = dayjs().subtract(weeksAgo, 'week').startOf('week').add(1, 'day'); // Monday
  const end = start.add(6, 'day'); // Sunday

  if (weeksAgo === 0) return 'This Week';
  if (weeksAgo === 1) return 'Last Week';

  // If both dates are in the same month, omit month for start date (e.g., "21-27 Jul")
  if (start.month() === end.month()) {
    return `${start.format('D')}-${end.format('D MMM')}`;
  }
  // If dates span different months, include month in both (e.g., "28 Jul - 3 Aug")
  return `${start.format('D MMM')} - ${end.format('D MMM')}`;
}
